import { useState } from 'react';
import { Alert, Box, Button, Chip, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { SpreadsheetPersonRow } from '@functions/read-spreadsheet';
import {
    EImportRowStatus, ImportConflictField, PeopleImportPreviewRow,
} from '../../services/responses/person.response';
import { FIELD_LABEL } from './import-columns-step';
import { ImportListedRow, ImportRowsDialog } from './import-rows-dialog';

/// Cabeçalho da coluna de detalhe na lista de cada contador; "novas" não tem o que explicar.
const DETAIL_LABEL: Partial<Record<EImportRowStatus, string>> = {
    [EImportRowStatus.SamePerson]: 'people.importar.noCrmComo',
    [EImportRowStatus.PhoneConflict]: 'people.importar.telefoneDe',
    [EImportRowStatus.RepeatedInFile]: 'people.importar.repeteALinha',
    [EImportRowStatus.EmailInUse]: 'people.importar.emailDe',
    [EImportRowStatus.Invalid]: 'people.importar.problema',
};

/// O que fazer com linha de telefone repetido ou de outra pessoa; não há "atualizar".
export type ConflictChoice = 'nophone' | 'fix' | 'skip';

/// Repetida na planilha é a mesma pessoa: entrar sem telefone criaria uma segunda.
/// Telefone de outra pessoa com nome diferente é gente diferente: entra sem número.
export const defaultChoice = (status: EImportRowStatus | undefined): ConflictChoice =>
    status === EImportRowStatus.RepeatedInFile ? 'skip' : 'nophone';

interface Props {
    rows: SpreadsheetPersonRow[];
    preview: Map<number, PeopleImportPreviewRow>;
    choices: Record<number, ConflictChoice>;
    phones: Record<number, string>;
    withLeads: boolean;
    /// Quadro escolhido: o mesmo contato pode ter lead aberto em outro quadro sem impedir este.
    boardId: string | null;
    onChoice: (line: number, choice: ConflictChoice) => void;
    onPhone: (line: number, phone: string) => void;
    onPhoneDone: () => void;
}

const sheetValue = (row: SpreadsheetPersonRow, field: ImportConflictField) => row[field];

const crmValue = (item: PeopleImportPreviewRow, field: ImportConflictField) => {
    switch (field) {
        case 'name': return item.matchName;
        case 'email': return item.matchEmail;
    }
};

/// Linhas que pedem decisão; novas e da mesma pessoa não aparecem uma a uma.
export const ImportReviewStep = ({
    rows, preview, choices, phones, withLeads, boardId, onChoice, onPhone, onPhoneDone,
}: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const count = (status: EImportRowStatus) =>
        rows.filter((row) => preview.get(row.line)?.status === status).length;

    const needsLook = (status: EImportRowStatus) =>
        status === EImportRowStatus.PhoneConflict
        || status === EImportRowStatus.RepeatedInFile
        || status === EImportRowStatus.EmailInUse
        || status === EImportRowStatus.Invalid;

    const openColumnOf = (item: PeopleImportPreviewRow | undefined) =>
        item?.openLeads.find((lead) => lead.boardId === boardId)?.columnName ?? null;

    // Linha com número corrigido continua na lista ao virar "nova": sumir pareceria perder a correção.
    const attention = rows.filter((row) => {
        const item = preview.get(row.line);
        return item && (needsLook(item.status) || phones[row.line] !== undefined);
    });

    const leadsBlocked = withLeads
        ? rows.filter((row) => {
            const item = preview.get(row.line);
            return openColumnOf(item)
                && (item.status === EImportRowStatus.New || item.status === EImportRowStatus.SamePerson);
        }).length
        : 0;

    const nameAt = (line: number | null) => rows.find((row) => row.line === line)?.name ?? '';

    const [listing, setListing] = useState<{ title: string; detailLabel?: string; rows: ImportListedRow[] } | null>(null);

    /// O que explica cada linha na lista do contador: quem ela já é no CRM, ou a linha que ela repete.
    const detailOf = (item: PeopleImportPreviewRow): string | null => {
        switch (item.status) {
            case EImportRowStatus.SamePerson:
            case EImportRowStatus.PhoneConflict:
                return item.matchName;
            case EImportRowStatus.RepeatedInFile:
                return item.repeatsLine ? `${item.repeatsLine} · ${nameAt(item.repeatsLine)}` : null;
            case EImportRowStatus.EmailInUse:
                return item.matchName ?? (item.repeatsLine ? `${item.repeatsLine} · ${nameAt(item.repeatsLine)}` : null);
            case EImportRowStatus.Invalid:
                return item.problems.join(' ');
            default:
                return null;
        }
    };

    const listOf = (filter: (item: PeopleImportPreviewRow) => boolean, detail: (item: PeopleImportPreviewRow) => string | null) =>
        rows
            .filter((row) => {
                const item = preview.get(row.line);
                return item && filter(item);
            })
            .map((row) => ({
                line: row.line,
                name: row.name,
                phone: phones[row.line] ?? row.phone,
                detail: detail(preview.get(row.line)!),
            }));

    const choiceOf = (row: SpreadsheetPersonRow) =>
        choices[row.line] ?? defaultChoice(preview.get(row.line)?.status);

    const choiceButtons = (row: SpreadsheetPersonRow) => (
        <>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={choiceOf(row)}
                onChange={(_, value) => value && onChoice(row.line, value)}
                sx={{ mt: 1.5, flexWrap: 'wrap' }}
            >
                <ToggleButton value="nophone" sx={{ px: 2 }}>{t('people.importar.semTelefone')}</ToggleButton>
                <ToggleButton value="fix" sx={{ px: 2 }}>{t('people.importar.numeroErrado')}</ToggleButton>
                <ToggleButton value="skip" sx={{ px: 2 }}>{t('people.importar.pular')}</ToggleButton>
            </ToggleButtonGroup>

            {choiceOf(row) === 'nophone' && (
                <Typography sx={{ fontSize: 12, color: cc.textMuted, mt: 0.75 }}>
                    {withLeads
                        ? t('people.importar.semTelefoneSemLead')
                        : t('people.importar.semTelefoneExplica')}
                </Typography>
            )}

            {choices[row.line] === 'fix' && phoneField(row)}
        </>
    );

    const phoneField = (row: SpreadsheetPersonRow) => (
        <TextField
            size="small"
            label={t('people.importar.numeroCorreto')}
            value={phones[row.line] ?? row.phone}
            onChange={(e) => onPhone(row.line, e.target.value)}
            onBlur={onPhoneDone}
            sx={{ mt: 1.25, width: 260 }}
        />
    );

    const chips: [EImportRowStatus, string, 'success' | 'info' | 'warning' | 'default' | 'error'][] = [
        [EImportRowStatus.New, 'novas', 'success'],
        [EImportRowStatus.SamePerson, 'mesmaPessoa', 'info'],
        [EImportRowStatus.PhoneConflict, 'telefoneDeOutra', 'warning'],
        [EImportRowStatus.RepeatedInFile, 'repetidas', 'default'],
        [EImportRowStatus.EmailInUse, 'mesmoEmail', 'default'],
        [EImportRowStatus.Invalid, 'comProblema', 'error'],
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                {chips.map(([status, key, color]) => (
                    <Chip
                        key={key}
                        label={t(`people.importar.${key}`, { total: count(status) })}
                        color={color}
                        variant="outlined"
                        onClick={count(status) > 0
                            ? () => setListing({
                                title: t(`people.importar.${key}`, { total: count(status) }),
                                detailLabel: DETAIL_LABEL[status] && t(DETAIL_LABEL[status]),
                                rows: listOf((item) => item.status === status, detailOf),
                            })
                            : undefined}
                        sx={{ '& .MuiChip-label': { color: cc.onTint } }}
                    />
                ))}
            </Box>

            {count(EImportRowStatus.SamePerson) > 0 && (
                <Typography sx={{ fontSize: 13, color: cc.textSecondary, mb: 0.75 }}>
                    {t('people.importar.mesmaPessoaExplica')}
                </Typography>
            )}

            {/* Aviso antes de importar: sem destaque, a tela dizia "tudo certo" e o lead não nascia. */}
            {leadsBlocked > 0 && (
                <Alert
                    severity="warning"
                    sx={{ mb: 1.5, alignItems: 'center' }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => setListing({
                                title: t('people.importar.leadsJaAbertos', { total: leadsBlocked }),
                                detailLabel: t('people.importar.colunaDoLead'),
                                rows: listOf(
                                    (item) => Boolean(openColumnOf(item))
                                        && (item.status === EImportRowStatus.New || item.status === EImportRowStatus.SamePerson),
                                    openColumnOf),
                            })}
                        >
                            {t('people.importar.verQuais')}
                        </Button>
                    }
                >
                    {t('people.importar.leadsJaAbertos', { total: leadsBlocked })}
                </Alert>
            )}

            {attention.length === 0 ? (
                <Typography sx={{ fontSize: 14, color: cc.textSecondary, py: 2 }}>
                    {t('people.importar.tudoCerto')}
                </Typography>
            ) : (
                <Box sx={{ border: `1px solid ${cc.border}`, borderRadius: '10px', overflow: 'hidden', mt: 1.5 }}>
                    {attention.map((row) => {
                        const item = preview.get(row.line)!;

                        return (
                            <Box
                                key={row.line}
                                sx={{ px: 2.5, py: 2, '& + &': { borderTop: `1px solid ${cc.divider}` } }}
                            >
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: cc.textMuted }}>
                                    {t('people.importar.linha', { linha: row.line })}
                                </Typography>

                                <Typography sx={{ fontSize: 15, fontWeight: 600, color: cc.textPrimary }}>
                                    {row.name || '—'} · {phones[row.line] ?? row.phone}
                                </Typography>

                                {item.status === EImportRowStatus.Invalid && (
                                    <Typography sx={{ fontSize: 13, color: cc.eventDanger, mt: 0.5 }}>
                                        {item.problems.join(' ')}
                                    </Typography>
                                )}

                                {item.status === EImportRowStatus.RepeatedInFile && (
                                    <>
                                        <Typography sx={{ fontSize: 13, color: cc.textSecondary, mt: 0.5 }}>
                                            {t('people.importar.repeteLinha', {
                                                linha: item.repeatsLine,
                                                nome: nameAt(item.repeatsLine),
                                            })}
                                        </Typography>
                                        {choiceButtons(row)}
                                    </>
                                )}

                                {item.status === EImportRowStatus.EmailInUse && (
                                    <Typography sx={{ fontSize: 13, color: cc.textSecondary, mt: 0.5 }}>
                                        {item.repeatsLine
                                            ? t('people.importar.mesmoEmailLinha', {
                                                linha: item.repeatsLine,
                                                nome: nameAt(item.repeatsLine),
                                            })
                                            : t('people.importar.emailJaCadastrado', { nome: item.matchName })}
                                    </Typography>
                                )}

                                {item.status === EImportRowStatus.PhoneConflict && (
                                    <>
                                        <Typography sx={{ fontSize: 13, color: cc.textSecondary, mt: 0.5 }}>
                                            {t('people.importar.telefoneJaEDe', { nome: item.matchName })}
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '140px 1fr 1fr',
                                                columnGap: 2,
                                                rowGap: 0.5,
                                                mt: 1.25,
                                                maxWidth: 720,
                                                fontSize: 13,
                                            }}
                                        >
                                            <Box />
                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: cc.textMuted }}>
                                                {t('people.importar.naPlanilha')}
                                            </Typography>
                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: cc.textMuted }}>
                                                {t('people.importar.noCrm')}
                                            </Typography>

                                            {item.conflicts.map((field) => [
                                                <Typography key={`${field}-l`} sx={{ fontSize: 13, color: cc.textSecondary }}>
                                                    {t(FIELD_LABEL[field])}
                                                </Typography>,
                                                <Typography key={`${field}-s`} sx={{ fontSize: 13, color: cc.textPrimary }}>
                                                    {sheetValue(row, field)}
                                                </Typography>,
                                                <Typography key={`${field}-c`} sx={{ fontSize: 13, color: cc.textPrimary }}>
                                                    {crmValue(item, field)}
                                                </Typography>,
                                            ])}
                                        </Box>

                                        {choiceButtons(row)}
                                    </>
                                )}

                                {(item.status === EImportRowStatus.New || item.status === EImportRowStatus.SamePerson) && (
                                    <>
                                        <Typography sx={{ fontSize: 13, color: cc.accent, mt: 0.5 }}>
                                            {item.status === EImportRowStatus.New
                                                ? t('people.importar.numeroCorrigido')
                                                : t('people.importar.numeroCorrigidoMesma', { nome: item.matchName })}
                                        </Typography>
                                        {phoneField(row)}
                                    </>
                                )}

                                {withLeads && openColumnOf(item) && item.status !== EImportRowStatus.Invalid && (
                                    <Typography sx={{ fontSize: 12, color: cc.textMuted, mt: 0.75 }}>
                                        {t('people.importar.jaTemLead', { coluna: openColumnOf(item) })}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}

            <ImportRowsDialog
                open={Boolean(listing)}
                title={listing?.title ?? ''}
                detailLabel={listing?.detailLabel}
                rows={listing?.rows ?? []}
                onClose={() => setListing(null)}
            />
        </Box>
    );
};
